import { useState, useCallback, useRef, useEffect } from 'react';
import useGrowers from '../../hooks/useGrowers';
import GrowerForm from '../../components/employee/GrowerForm';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import { formatDate, formatPhone } from '../../utils/formatters';
import './EmployeeListPage.css';
import './Growers.css';

const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const EmployeeGrowers = () => {
  // ── View state ───────────────────────────────────────────
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [editingGrower, setEditingGrower] = useState(null);

  // ── List filters ─────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const debounceRef = useRef(null);

  // Refs to avoid stale closures in debounced callbacks
  const activeFilterRef = useRef(activeFilter);
  // eslint-disable-next-line react-hooks/refs
  activeFilterRef.current = activeFilter;

  // ── Alerts ───────────────────────────────────────────────
  const [alert, setAlert] = useState(null);

  // ── Confirm modal (deactivate or reactivate) ────────────
  const [confirmAction, setConfirmAction] = useState(null); // { grower, type: 'deactivate' | 'reactivate' }

  // ── Data hook ────────────────────────────────────────────
  const {
    growers,
    loading,
    isRefreshing,
    isSaving,
    isDeleting,
    error,
    pagination,
    choices,
    fetchGrowers,
    fetchGrower,
    saveGrower,
    removeGrower,
    reactivateGrower,
    clearError,
  } = useGrowers();

  // ── Build current params (reads refs for fresh state) ────
  const buildParams = useCallback(
    (overrides = {}) => ({
      search: searchTerm,
      is_active: activeFilterRef.current,
      page: pagination.page,
      ...overrides,
    }),
    [searchTerm, pagination.page]
  );

  // ── Debounced search ─────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchGrowers({ search: val, is_active: activeFilterRef.current, page: 1 });
    }, 300);
  };

  // ── Filter by active status ──────────────────────────────
  const handleFilterChange = (value) => {
    setActiveFilter(value);
    fetchGrowers(buildParams({ is_active: value, page: 1 }));
  };

  // ── Pagination ───────────────────────────────────────────
  const handlePageChange = (page) => {
    fetchGrowers(buildParams({ page }));
  };

  // ── Row click → edit ─────────────────────────────────────
  const handleRowClick = async (row) => {
    try {
      const full = await fetchGrower(row.id);
      setEditingGrower(full);
      setView('form');
      setAlert(null);
    } catch {
      setAlert({ type: 'error', message: 'Failed to load grower details.' });
    }
  };

  // ── Add new ──────────────────────────────────────────────
  const handleAddNew = () => {
    setEditingGrower(null);
    setView('form');
    setAlert(null);
  };

  // ── Cancel form ──────────────────────────────────────────
  const handleCancel = () => {
    setEditingGrower(null);
    setView('list');
  };

  // ── Save (create or update) ──────────────────────────────
  const handleSave = async (data) => {
    try {
      const id = editingGrower?.id || null;
      await saveGrower(data, id);
      setView('list');
      setEditingGrower(null);
      setAlert({
        type: 'success',
        message: id ? 'Grower updated successfully.' : 'Grower created successfully.',
      });
      fetchGrowers(buildParams({ page: 1 }));
    } catch (err) {
      setAlert({
        type: 'error',
        message: err?.message || 'Failed to save grower.',
      });
    }
  };

  // ── Deactivate / Reactivate ──────────────────────────────
  const handleStatusAction = (type) => {
    if (editingGrower) setConfirmAction({ grower: editingGrower, type });
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { grower: target, type } = confirmAction;
    try {
      if (type === 'deactivate') {
        await removeGrower(target.id);
      } else {
        await reactivateGrower(target.id);
      }
      setConfirmAction(null);
      setView('list');
      setEditingGrower(null);
      setAlert({
        type: 'success',
        message: type === 'deactivate'
          ? `"${target.farm_name}" deactivated.`
          : `"${target.farm_name}" reactivated.`,
      });
      fetchGrowers(buildParams({ page: 1 }));
    } catch {
      setAlert({
        type: 'error',
        message: `Failed to ${type} grower.`,
      });
      setConfirmAction(null);
    }
  };

  // ── Clean up debounce timer on unmount ──────────────────
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  // ── Clear hook errors into alert ─────────────────────────
  useEffect(() => {
    if (error) {
      setAlert({ type: 'error', message: error.message || 'An error occurred.' });
      clearError();
    }
  }, [error, clearError]);

  // ── Table columns ────────────────────────────────────────
  const columns = [
    { key: 'farm_name', label: 'Farm Name' },
    { key: 'region', label: 'Region' },
    {
      key: 'is_active',
      label: 'Status',
      render: (val) => (
        <span className={`emp-list__badge emp-list__badge--${val ? 'active' : 'inactive'}`}>
          {val ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'certifications_display',
      label: 'Certifications',
      sortable: false,
      render: (val) => (val && val.length > 0 ? val.join(', ') : '\u2014'),
    },
    {
      key: 'email',
      label: 'Email',
      render: (val) => val || '\u2014',
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (val) => formatDate(val),
    },
  ];

  // ── Render ───────────────────────────────────────────────

  return (
    <div className="employee-page growers-page">
      {/* ── Alert ──────────────────────────────────────── */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          dismissible
          onDismiss={() => setAlert(null)}
          className="emp-list__alert"
        />
      )}

      {view === 'list' ? (
        <>
          {/* ── Header ───────────────────────────────────── */}
          <div className="employee-page__header">
            <p className="employee-page__eyebrow">Employee Portal</p>
            <h1 className="employee-page__title">Growers</h1>
          </div>

          {/* ── Toolbar ──────────────────────────────────── */}
          <div className="emp-list__toolbar">
            <div className="emp-list__toolbar-left">
              <div className="emp-list__search-wrap">
                <svg className="emp-list__search-icon" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M8.5 3a5.5 5.5 0 014.383 8.823l3.896 3.9a.75.75 0 01-1.06 1.06l-3.9-3.896A5.5 5.5 0 118.5 3zm0 1.5a4 4 0 100 8 4 4 0 000-8z" fill="currentColor" />
                </svg>
                <input
                  type="text"
                  className="emp-list__search"
                  placeholder="Search farms, regions, email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  aria-label="Search growers"
                />
              </div>
              <div className="emp-list__filters">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    className={`emp-list__filter ${activeFilter === f.value ? 'emp-list__filter--active' : ''}`}
                    onClick={() => handleFilterChange(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <Button variant="primary" size="small" onClick={handleAddNew}>
              + Add Grower
            </Button>
          </div>

          {/* ── Content body ─────────────────────────────── */}
          <div className="emp-list__body">
            {loading ? (
              <Loading text="Loading growers..." />
            ) : (
              <>
                {/* Desktop table (hidden on mobile) */}
                <Table
                  columns={columns}
                  data={growers}
                  onRowClick={handleRowClick}
                  emptyMessage="No growers found."
                  className="emp-list__table"
                />

                {/* Mobile card list (hidden on desktop) */}
                <div className="emp-list__cards">
                  {growers.length === 0 ? (
                    <p className="emp-list__cards-empty">No growers found.</p>
                  ) : (
                    growers.map((grower) => (
                      <button
                        key={grower.id}
                        type="button"
                        className="emp-list__card"
                        onClick={() => handleRowClick(grower)}
                      >
                        <div className="emp-list__card-top">
                          <span className="emp-list__card-name">{grower.farm_name}</span>
                          <span className={`emp-list__badge emp-list__badge--${grower.is_active ? 'active' : 'inactive'}`}>
                            {grower.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="emp-list__card-subtitle">{grower.region}</div>
                        {grower.certifications_display?.length > 0 && (
                          <div className="emp-list__card-tags">
                            {grower.certifications_display.map((cert) => (
                              <span key={cert} className="emp-list__card-tag">{cert}</span>
                            ))}
                          </div>
                        )}
                        <div className="growers-card__meta">
                          {grower.email && (
                            <span className="growers-card__meta-item">{grower.email}</span>
                          )}
                          {grower.phone && (
                            <span className="growers-card__meta-item">{formatPhone(grower.phone)}</span>
                          )}
                        </div>
                        <div className="emp-list__card-date">{formatDate(grower.created_at)}</div>
                        <svg className="emp-list__card-chevron" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M6 4L10 8L6 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ))
                  )}
                </div>

                <Pagination
                  currentPage={pagination.page}
                  totalCount={pagination.count}
                  pageSize={PAGE_SIZE}
                  onPageChange={handlePageChange}
                />
              </>
            )}
            {isRefreshing && (
              <div className="emp-list__refreshing" />
            )}
          </div>
        </>
      ) : (
        <>
          {/* ── Form header ──────────────────────────────── */}
          <div className="employee-page__header">
            <p className="employee-page__eyebrow">Employee Portal</p>
            <h1 className="employee-page__title">
              {editingGrower ? `Edit: ${editingGrower.farm_name}` : 'Add Grower'}
            </h1>
          </div>

          {/* ── Form body ────────────────────────────────── */}
          <div className="emp-list__body emp-list__body--form">
            {loading ? (
              <Loading text="Loading..." />
            ) : (
              <>
                <GrowerForm
                  grower={editingGrower}
                  choices={choices}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isSaving={isSaving}
                />
                {editingGrower && (
                  editingGrower.is_active ? (
                    <div className="emp-list__danger-zone">
                      <div className="emp-list__danger-info">
                        <span className="emp-list__danger-label">Danger zone</span>
                        <span className="emp-list__danger-desc">
                          Deactivate this grower. They will no longer appear on the public site.
                        </span>
                      </div>
                      <Button
                        variant="danger"
                        size="small"
                        onClick={() => handleStatusAction('deactivate')}
                        disabled={isDeleting || isSaving}
                      >
                        {isDeleting ? 'Deactivating...' : 'Deactivate Grower'}
                      </Button>
                    </div>
                  ) : (
                    <div className="emp-list__reactivate-zone">
                      <div className="emp-list__danger-info">
                        <span className="emp-list__reactivate-label">Inactive</span>
                        <span className="emp-list__danger-desc">
                          This grower is currently inactive and hidden from the public site.
                        </span>
                      </div>
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => handleStatusAction('reactivate')}
                        disabled={isSaving}
                      >
                        {isSaving ? 'Reactivating...' : 'Reactivate Grower'}
                      </Button>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── Confirmation modal (deactivate or reactivate) ── */}
      <Modal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        title={confirmAction?.type === 'reactivate' ? 'Confirm Reactivation' : 'Confirm Deactivation'}
        size="small"
      >
        <p className="emp-list__confirm-text">
          {confirmAction?.type === 'reactivate' ? (
            <>
              Are you sure you want to reactivate <strong>{confirmAction?.grower?.farm_name}</strong>?
              They will appear on the public site again.
            </>
          ) : (
            <>
              Are you sure you want to deactivate <strong>{confirmAction?.grower?.farm_name}</strong>?
              They will be hidden from the public site but can be reactivated later.
            </>
          )}
        </p>
        <div className="emp-list__confirm-actions">
          <Button variant="secondary" size="small" onClick={() => setConfirmAction(null)}>
            Cancel
          </Button>
          <Button
            variant={confirmAction?.type === 'reactivate' ? 'primary' : 'danger'}
            size="small"
            onClick={handleConfirm}
            disabled={isDeleting || isSaving}
          >
            {confirmAction?.type === 'reactivate'
              ? (isSaving ? 'Reactivating...' : 'Reactivate')
              : (isDeleting ? 'Deactivating...' : 'Deactivate')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeGrowers;
